import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-[9px] text-[13px] font-semibold transition-[color,background-color,border-color,box-shadow,transform,scale] duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pink/60 focus-visible:ring-offset-2 focus-visible:ring-offset-bg active:scale-[0.98] disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        // the one strong element on screen — light pink fill, ink text
        default: "bg-accent-hot text-[#10082b] hover:bg-pink hover:shadow-[0_4px_14px_rgba(242,184,208,0.14)]",
        destructive: "bg-danger/15 text-danger hover:bg-danger/25",
        outline: "border border-border bg-transparent text-text hover:border-border-strong hover:bg-surface-2",
        ghost: "text-text-muted hover:bg-surface-2 hover:text-text",
        subtle: "bg-surface-2 text-text hover:bg-surface-3",
        link: "text-pink underline-offset-4 hover:text-accent-hot hover:underline",
      },
      size: {
        default: "h-9 px-4",
        sm: "h-8 rounded-[8px] px-3 text-xs",
        lg: "h-10 px-5 text-sm",
        icon: "h-9 w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
